/**
 * lazyWithRetry — wraps React.lazy() with automatic retry on ChunkLoadError.
 *
 * On chunk load failure the browser may have a stale service-worker or CDN cache.
 * One reload attempt per 60-second window is enough to recover in most cases.
 * If the retry also fails, the error propagates normally to the nearest ErrorBoundary.
 */
import React from 'react';

const RETRY_COOLDOWN_MS = 60_000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().catch((err: unknown) => {
      const isChunkError =
        err instanceof Error &&
        (err.name === 'ChunkLoadError' ||
          err.message?.includes('Loading chunk') ||
          err.message?.includes('Unexpected token') ||
          err.message?.includes('Failed to fetch dynamically imported module'));

      if (isChunkError) {
        const key = 'lazy_retry_ts';
        const lastAttempt = sessionStorage.getItem(key);
        const now = Date.now();

        if (!lastAttempt || now - parseInt(lastAttempt, 10) > RETRY_COOLDOWN_MS) {
          sessionStorage.setItem(key, now.toString());
          // Reload the page once so the browser fetches fresh chunks.
          window.location.reload();
          // Return a never-resolving promise — the reload will handle it.
          return new Promise<never>(() => undefined);
        }
      }

      throw err;
    })
  );
}
